import { useEffect, useMemo, useState } from 'react';
import { useWatchlistMapData } from './useWatchlistMapData';
import {
  hasCompanyRoutesContent,
  mapCompanyRoutesToWatchlistItems,
} from '../utils/tenantGraphContextAdapter';
import {
  buildCoordinateLookup,
  buildGeoRegionMarkers,
  buildStaticMapMarkers,
  buildStaticMapRoutes,
  computeMapBounds,
  enrichMarkersWithGeocoding,
  mergeMapMarkers,
  mergeMapRoutes,
} from '../utils/tenantMapModel';

const MAX_FACILITY_GEOCODE_MS = 25000;
const EMPTY_WATCHLIST = [];

export function useTenantMapData(
  nodesByKey,
  {
    companyRoutes = {},
    watchlistItems = [],
    riskAnalyses = {},
    enableGeocoding = true,
    enableSupplierRoutes = true,
  } = {}
) {
  const [geocodedMarkers, setGeocodedMarkers] = useState([]);
  const [facilityGeocoding, setFacilityGeocoding] = useState(false);

  const graphContextItems = useMemo(
    () => mapCompanyRoutesToWatchlistItems(companyRoutes),
    [companyRoutes]
  );

  const hasGraphSupplierData = useMemo(
    () => hasCompanyRoutesContent(companyRoutes),
    [companyRoutes]
  );

  const supplierItems = useMemo(() => {
    if (hasGraphSupplierData) return graphContextItems;
    return watchlistItems;
  }, [hasGraphSupplierData, graphContextItems, watchlistItems]);

  const supplierKey = useMemo(
    () =>
      supplierItems
        .map((item) => item.companyId ?? item.company_id)
        .filter(Boolean)
        .join('|'),
    [supplierItems]
  );

  const supplierMap = useWatchlistMapData(
    enableSupplierRoutes ? supplierItems : EMPTY_WATCHLIST,
    riskAnalyses,
    supplierKey
  );

  const staticMarkers = useMemo(() => buildStaticMapMarkers(nodesByKey), [nodesByKey]);
  const geoMarkers = useMemo(() => buildGeoRegionMarkers(nodesByKey), [nodesByKey]);

  const facilityFingerprint = useMemo(() => {
    const facilities = Array.isArray(nodesByKey.facilities) ? nodesByKey.facilities : [];
    const supplierFacilities = Array.isArray(nodesByKey.supplierFacilities)
      ? nodesByKey.supplierFacilities
      : [];
    return [
      facilities.length,
      supplierFacilities.length,
      ...facilities.map((row) => row?.facility_id ?? row?.id ?? ''),
      ...supplierFacilities.map((row) => row?.facility_id ?? row?.id ?? ''),
    ].join('|');
  }, [nodesByKey.facilities, nodesByKey.supplierFacilities]);

  useEffect(() => {
    if (!enableGeocoding) {
      setGeocodedMarkers((prev) => (prev.length === 0 ? prev : []));
      setFacilityGeocoding((prev) => (prev === false ? prev : false));
    }
  }, [enableGeocoding]);

  useEffect(() => {
    let cancelled = false;

    if (!enableGeocoding) {
      return undefined;
    }

    const run = async () => {
      setFacilityGeocoding(true);
      try {
        const geocodePromise = enrichMarkersWithGeocoding(nodesByKey, staticMarkers);
        const timeoutPromise = new Promise((resolve) => {
          setTimeout(() => resolve(staticMarkers), MAX_FACILITY_GEOCODE_MS);
        });
        const markers = await Promise.race([geocodePromise, timeoutPromise]);
        if (!cancelled) setGeocodedMarkers(markers);
      } finally {
        if (!cancelled) setFacilityGeocoding(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [facilityFingerprint, enableGeocoding, nodesByKey, staticMarkers]);

  const facilityMarkers = useMemo(() => {
    const byId = new Map();
    staticMarkers.forEach((marker) => byId.set(marker.id, marker));
    geocodedMarkers.forEach((marker) => byId.set(marker.id, marker));
    return Array.from(byId.values());
  }, [staticMarkers, geocodedMarkers]);

  const lookup = useMemo(() => buildCoordinateLookup(facilityMarkers), [facilityMarkers]);

  const nodeRoutes = useMemo(
    () => buildStaticMapRoutes(nodesByKey, lookup),
    [nodesByKey, lookup]
  );

  const markers = useMemo(
    () => mergeMapMarkers(facilityMarkers, supplierMap.markers, geoMarkers),
    [facilityMarkers, supplierMap.markers, geoMarkers]
  );

  const routes = useMemo(
    () => mergeMapRoutes(nodeRoutes, supplierMap.routes),
    [nodeRoutes, supplierMap.routes]
  );

  const bounds = useMemo(
    () => computeMapBounds(markers, routes) || supplierMap.bounds,
    [markers, routes, supplierMap.bounds]
  );

  const geocoding = supplierMap.geocoding || facilityGeocoding;

  return {
    markers,
    facilityMarkers,
    geoMarkers,
    routes,
    bounds,
    geocoding,
    mapReady: markers.length > 0 || routes.length > 0,
    hasMapContent: markers.length > 0 || routes.length > 0,
  };
}
